/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import React, {Fragment} from 'react'
import {currencyNumberFormat, translate} from 'utils/utility'
import {useTranslation} from 'react-i18next'
import {PackagesCartItem} from 'types/entities/PackagesCartItem'
import {ArrowsRightLeftIcon, BanknotesIcon, MinusIcon, PencilIcon, PlusIcon, TagIcon, TrashIcon,} from '@heroicons/react/24/outline'
import {PackageItemPersonType} from 'types/enums/PackageItemPersonType'
import {closeSecondaryModal, openModal, openSecondaryModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import {useTypedSelector} from 'helpers/reducers'
import EditPackageCartItemModal from 'components/Booking/EditPackageCartItemModal'
import EditOfferItemModal from 'components/Offers/EditOfferItemModal'
import {OffersAction} from 'store/offers/OffersAction'
import BookingSellerEditReservationItemModal from './BookingSellerEditReservationItemModal'
import BookingLoginAction from 'store/bookingLogin/BookingLoginAction'
import {ModalSize} from 'types/enums/ModalSize'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {toast} from 'utils/toastUtils'
import UiTable, {UiTableBody, UiTableCell, UiTableHead, UiTableHeaderCell, UiTableRow,} from 'components/Ui/UiTable'
import {usePackagesCartItemSorting} from "utils/packageUtils";
import {Tooltip} from 'react-tooltip'
import type {Offer} from 'types/entities/Offer'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import PackagesCartItemVoucher from './PackagesCartItemVoucher'
import {Translations} from 'helpers/config'
import type {PackageItemVariant} from "types/entities/PackageItem";
import PackageUpsellSlideOver from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSlideOver'
import UiCounter from 'components/Ui/UiCounter'
import UiCurrencyInput from 'components/Ui/UiCurrencyInput'
import UiTextarea from 'components/Ui/UiTextarea'
import {
    getOfferItemPresentation,
    getPackagesCartItemDisplayName,
} from 'utils/offers/offerItemPresentation'
import UiRoundedContent from "components/Ui/UiRoundedContent";

const OFFER_DETAIL_CALC_EDIT_DISABLED_TOOLTIP_ID = 'offer-detail-calc-edit-disabled-tooltip'

interface BookingSellerRecapitulationPackageCartItemsTableProps {
    // packagesCartItems: PackagesCartItem[]
    // packagesCartItemVouchers: PackagesCartItemVoucher[]
    title: string
    // packagesCart?: PackagesCart
    offer: Offer
    calculation: OfferCalculation
    disabled: boolean
    // calculationAdults?: number
    // calculationChildren?: number
    // onDeleteItem?: (item: PackagesCartItem) => void
    // disabled?: boolean
    // isNetto?: boolean
    // calculationPriceTotal?: number
    // discount?: number
}

type PackagesCartItemWithVariant = PackagesCartItem & {
    packageItemVariant?: PackageItemVariant | null
}

const BookingSellerRecapitulationPackageCartItemsTable = (
    props: BookingSellerRecapitulationPackageCartItemsTableProps
) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isSecondaryModalOpen = useTypedSelector((state) => state.ui.secondaryModal.isOpen)
    const secondaryModalTitle = useTypedSelector((state) => state.ui.secondaryModal.title)
    const packagesCart = props.calculation.packagesCart
    const isOfferDetailInSecondaryModal = isSecondaryModalOpen && secondaryModalTitle === t('Offer Detail')
    const [editingOfferItem, setEditingOfferItem] = React.useState<PackagesCartItem | null>(null)
    const [editingQuantity, setEditingQuantity] = React.useState<number>(0)
    const [editingUnitPrice, setEditingUnitPrice] = React.useState<number>(0)
    const [editingCustomText, setEditingCustomText] = React.useState<string>('')
    const [isSavingEditItem, setIsSavingEditItem] = React.useState(false)

    const { sortedItems } = usePackagesCartItemSorting()

    const openInlineEditPopup = (packagesCartItem: PackagesCartItem) => {
        const currentQuantity = packagesCartItem.drivers_count_adult + packagesCartItem.drivers_count_child
        setEditingOfferItem(packagesCartItem)
        setEditingQuantity(currentQuantity)
        setEditingUnitPrice(currentQuantity > 0 ? (packagesCartItem.price || 0) / currentQuantity / 100 : 0)
        setEditingCustomText(packagesCartItem.custom_text || '')
    }

    const saveInlineEditPopup = async () => {
        const offerId = props.offer.id ?? props.calculation.offer_id

        if (!editingOfferItem || !offerId) {
            toast.error(t('Unable to save item edit'))
            return
        }

        setIsSavingEditItem(true)
        try {
            await dispatch(
                OffersAction.fetchUpdateOfferItem({
                    offer_id: offerId,
                    packages_cart_item_id: editingOfferItem.id,
                    packages_cart_id: editingOfferItem.packages_cart_id,
                    old_package_item_id: editingOfferItem.package_item_id,
                    package_item_id: editingOfferItem.package_item_id,
                    quantity: Math.max(0, editingQuantity),
                    unit_price: Math.round(Math.max(0, editingUnitPrice) * 100),
                    custom_text: editingCustomText,
                })
            )
            toast.success(t('Item updated'))
            await dispatch(OffersAction.fetchOfferById(offerId))
            setEditingOfferItem(null)
        } catch (e) {
            console.error(e)
            toast.error(t('Failed to update item'))
        } finally {
            setIsSavingEditItem(false)
        }
    }
    const onUpdateQuantity = async (item: PackagesCartItem, newQuantity: number) => {
        if (!props.offer.id || newQuantity < 1) return
        try {
            const currentQuantity = item.drivers_count_adult + item.drivers_count_child
            if (currentQuantity === 0) return

            const unitPrice = item.price / currentQuantity

            await dispatch(
                OffersAction.fetchUpdateOfferItem({
                    offer_id: props.offer.id,
                    packages_cart_item_id: item.id,
                    packages_cart_id: item.packages_cart_id,
                    old_package_item_id: item.package_item_id,
                    package_item_id: item.package_item_id,
                    quantity: newQuantity,
                    unit_price: unitPrice,
                    custom_text: item.custom_text || undefined,
                })
            )
            toast.success(t('Quantity updated'))
        } catch (e) {
            console.error(e)
            toast.error(t('Failed to update quantity'))
        }
    }

    const editPackagesCartItemHandler = (packagesCartItem: PackagesCartItem) => {
        if (props.offer.id) {
            openInlineEditPopup(packagesCartItem)
        } else if (props.calculation.packagesCart) {
            if (props.calculation.packagesCart.reservation_id) {
                dispatch(
                    openSecondaryModal(
                        <BookingSellerEditReservationItemModal
                            reservationId={props.calculation.packagesCart.reservation_id}
                            item={packagesCartItem}
                            onSuccess={() => {
                                if (props.calculation.packagesCart?.reservation_id) {
                                    dispatch(
                                        BookingLoginAction.fetchSellerReservationDetail(
                                            props.calculation.packagesCart.reservation_id
                                        )
                                    )
                                }
                            }}
                        />,
                        {
                            modalSize: ModalSize.Medium,
                        },
                        t('Edit Item')
                    )
                )
            } else {
                dispatch(
                    openModal(
                        <EditPackageCartItemModal
                            packagesCart={props.calculation.packagesCart}
                            item={packagesCartItem}
                        />,
                        {
                            modalSize: ModalSize.Small,
                        }
                    )
                )
            }
        }
    }

    const replacePackagesCartItemHandler = (packagesCartItem: PackagesCartItem) => {
        if (!props.offer.id) {
            return
        }

        if (isOfferDetailInSecondaryModal) {
            dispatch(
                openModal(
                    <EditOfferItemModal
                        offerId={props.offer.id}
                        item={packagesCartItem}
                        packagesCart={props.calculation.packagesCart}
                        closeWithSecondaryModal={false}
                        onSuccess={() => {
                            dispatch(OffersAction.fetchOfferById(props.offer.id!))
                        }}
                    />,
                    {
                        modalSize: ModalSize.Medium,
                    },
                    t('Edit Item')
                )
            )
            return
        }

        dispatch(
            openSecondaryModal(
                <EditOfferItemModal
                    offerId={props.offer.id}
                    item={packagesCartItem}
                    packagesCart={props.calculation.packagesCart}
                    onSuccess={() => {
                        dispatch(OffersAction.fetchOfferById(props.offer.id!))
                    }}
                />,
                {
                    modalSize: ModalSize.Medium,
                },
                t('Edit Item')
            )
        )
    }

    const deletePackagesCartItemHandler = (
        e: React.MouseEvent<HTMLButtonElement>,
        item: PackagesCartItem
    ) => {
        e.stopPropagation()
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Item')}
                    message={t(
                        'Are you sure you want to delete this item from the calculation? This action cannot be undone.'
                    )}
                    onConfirm={async () => {
                        try {
                            const data = {
                                offer_id: props.offer.id,
                                packages_cart_id: item.packages_cart_id,
                                package_item_id: item.package_item_id,
                            }
                            await dispatch(OffersAction.fetchDeleteOfferItem(data))
                            toast.success(t('Item deleted successfully'))
                            dispatch(closeSecondaryModal())
                        } catch (error) {
                            console.error(error)
                            toast.error(t('Failed to delete item'))
                            dispatch(closeSecondaryModal())
                        }
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                { modalSize: ModalSize.Small },
                t('Confirm')
            )
        )
    }

    if (!packagesCart) {
        return (
            <div
                className={clsx('BookingSellerRecapitulationPackageCartItemsTable bg-gray-50 dark:bg-gray-900 rounded-lg')}
            >
                <div className={clsx('overflow-x-auto relative')}>
                    <UiTable>
                        <UiTableBody>
                            <UiTableRow key="no-cart">
                                <UiTableCell align="center" colSpan={3}>
                                    {t('No cart assigned to this calculation')}
                                </UiTableCell>
                            </UiTableRow>
                        </UiTableBody>
                    </UiTable>
                </div>
            </div>
        )
    }

    const editingPresentation = editingOfferItem
        ? getOfferItemPresentation(editingOfferItem.packageItem)
        : null
    const editingTotalPrice = editingQuantity * editingUnitPrice

    return (
        <div
            className={clsx('BookingSellerRecapitulationPackageCartItemsTable bg-gray-50 dark:bg-gray-900 rounded-lg')}
        >
            {props.title && (
                <h2
                    className={clsx(
                        'BookingCardTitle font-bold text-xl text-gray-900 dark:text-gray-100 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2 mb-3'
                    )}
                >
                    <div className={clsx('inline-flex items-center gap-x-2')}>
                        <span>{t(props.title)}</span>
                    </div>
                </h2>
            )}

            <div className={clsx('overflow-x-auto relative')}>
                <UiTable>
                    <UiTableHead>
                        <UiTableHeaderCell>
                            <div className={clsx('flex items-center')}>
                                <TagIcon className={clsx('w-4 h-4 mr-1.5')} />
                                <span>{t('Item')}</span>
                            </div>
                        </UiTableHeaderCell>
                        <UiTableHeaderCell align={'left'} colSpan={2}>
                            <div className={clsx('inline-flex')}>
                                <BanknotesIcon className={clsx('w-4 h-4 mr-1.5')} />
                                <span>
                                    {t('Price')}
                                    {props.calculation.is_netto ? ` (${t('netto')})` : ''}
                                </span>
                            </div>
                        </UiTableHeaderCell>
                    </UiTableHead>
                    <UiTableBody>
                        {packagesCart.packagesCartItems.length > 0 &&
                            sortedItems(packagesCart.packagesCartItems).map(
                                (packagesCartItem, index) => {
                                    const cartItemDriversCount =
                                        packagesCartItem.drivers_count_adult + packagesCartItem.drivers_count_child
                                    if (!packagesCartItem.packageItem?.article_id) {
                                        return null
                                    }
									const hasVariant = 'packageItemVariant' in packagesCartItem
                                    const variant: PackageItemVariant | null = hasVariant
                                        ? (packagesCartItem as PackagesCartItemWithVariant)
                                              .packageItemVariant ?? null
                                        : null
									const variantName: Translations | null = variant ? variant.name : null;
                                    return (
                                        <Fragment key={`item-${packagesCartItem.id || index}`}>
                                            <UiTableRow>
                                                <UiTableCell fontWeight="medium" className={clsx('space-x-1')}>
                                                    <span>{cartItemDriversCount} x</span>
                                                    {packagesCartItem.packageItem?.name && (
                                                        <div className="flex flex-col">
                                                            <div className="flex items-center gap-x-1">
                                                                {variantName && <b>{translate(variantName)}</b>}{' '}
                                                                <span>
                                                                    {getPackagesCartItemDisplayName(
                                                                        packagesCartItem
                                                                    )}
                                                                </span>

                                                                {packagesCartItem.packageItem.person_type ===
                                                                    PackageItemPersonType.Child && (
                                                                    <small className="text-gray-500">
                                                                        ({t('children')})
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </UiTableCell>
                                                <UiTableCell align="right">
                                                    <div className={'flex-col flex'}>
                                                        {packagesCartItem.price &&
                                                            currencyNumberFormat(
                                                                props.calculation.is_netto
                                                                    ? packagesCartItem.price /
                                                                          (1 + +packagesCartItem.tax_rate)
                                                                    : packagesCartItem.price
                                                            )}
                                                        <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                                                            (
                                                            {currencyNumberFormat(
                                                                cartItemDriversCount > 0
                                                                    ? (props.calculation.is_netto
                                                                          ? packagesCartItem.price /
                                                                            (1 + +packagesCartItem.tax_rate)
                                                                          : packagesCartItem.price) /
                                                                          cartItemDriversCount
                                                                    : 0
                                                            )}
                                                            )
                                                        </span>
                                                    </div>
                                                </UiTableCell>
                                                <UiTableCell align="right">
                                                    <div className="flex gap-1.5 justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                editPackagesCartItemHandler(packagesCartItem)
                                                            }
                                                            disabled={
                                                                (!packagesCart && !props.offer) ||
                                                                props.disabled
                                                            }
                                                            aria-label={t('Edit Item') as string}
                                                            title={t('Edit Item') as string}
                                                            className="p-1 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            <PencilIcon className="w-4 h-4 text-white" />
                                                        </button>

                                                        {props.offer && !props.disabled && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    replacePackagesCartItemHandler(packagesCartItem)
                                                                }
                                                                className="p-1 bg-indigo-500 hover:bg-indigo-600 dark:bg-indigo-600 dark:hover:bg-indigo-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                                aria-label={t('Replace Item') as string}
                                                                title={t('Replace Item') as string}
                                                            >
                                                                <ArrowsRightLeftIcon className="w-4 h-4 text-white" />
                                                            </button>
                                                        )}

                                                        {props.offer && !props.disabled && (
                                                            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded p-0.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        onUpdateQuantity(
                                                                            packagesCartItem,
                                                                            cartItemDriversCount - 1
                                                                        )
                                                                    }
                                                                    disabled={cartItemDriversCount <= 1}
                                                                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    aria-label={t('Decrease quantity') as string}
                                                                    title={t('Decrease quantity') as string}
                                                                >
                                                                    <MinusIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        onUpdateQuantity(
                                                                            packagesCartItem,
                                                                            cartItemDriversCount + 1
                                                                        )
                                                                    }
                                                                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors text-gray-600 dark:text-gray-400"
                                                                    aria-label={t('Increase quantity') as string}
                                                                    title={t('Increase quantity') as string}
                                                                >
                                                                    <PlusIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}

                                                        {props.offer && !props.disabled && (
                                                            <button
                                                                type="button"
                                                                className="p-1 bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 rounded transition-colors"
                                                                aria-label={t('Delete Item') as string}
                                                                title={t('Delete Item')}
                                                                onClick={(e) =>
                                                                    deletePackagesCartItemHandler(e, packagesCartItem)
                                                                }
                                                            >
                                                                <TrashIcon className="w-4 h-4 text-white" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </UiTableCell>
                                            </UiTableRow>
                                            {packagesCartItem.custom_text && (
                                                <UiTableRow key={`item-custom-text-${packagesCartItem.id || index}`}>
                                                    <UiTableCell colSpan={3}>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400 font-normal mt-0.5 italic">
                                                            {packagesCartItem.custom_text}
                                                        </div>
                                                    </UiTableCell>
                                                </UiTableRow>
                                            )}
                                        </Fragment>
                                    )
                                }
                            )}

                        <PackagesCartItemVoucher
                            packagesCart={packagesCart}
                            offer={props.offer}
                            calculation={props.calculation}
                            disabled={props.disabled}
                        />
                        {packagesCart.packagesCartItems.length > 0 && (
                            <UiTableRow key="total-price-row">
                                <UiTableCell fontWeight="medium">
                                    <span>
                                        {t('Total price')}
                                        {props.calculation.is_netto ? ` (${t('netto')})` : ''}
                                    </span>
                                </UiTableCell>
                                <UiTableCell align="right">
                                    {currencyNumberFormat(
                                        packagesCart.price *
                                            (props.calculation.is_netto ? 1 / 1.081 : 1)
                                    )}
                                </UiTableCell>
                                <UiTableCell>
                                    <span />
                                </UiTableCell>
                            </UiTableRow>
                        )}
                        {packagesCart.packagesCartItems.length > 0 && (() => {
                            const personsCount = (packagesCart.drivers_count_adult ?? 0) + (packagesCart.drivers_count_child ?? 0)
                            if (personsCount <= 0) return null
                            const bruttoPerPerson = packagesCart.price / personsCount
                            const nettoPerPerson = (packagesCart.price / 1.081) / personsCount
                            return (
                                <>
                                    <UiTableRow key="price-per-person-brutto-row">
                                        <UiTableCell fontWeight="medium">
                                            <span>{t('Price per participant')} ({t('brutto')})</span>
                                        </UiTableCell>
                                        <UiTableCell align="right">
                                            {currencyNumberFormat(bruttoPerPerson)}
                                        </UiTableCell>
                                        <UiTableCell>
                                            <span />
                                        </UiTableCell>
                                    </UiTableRow>
                                    <UiTableRow key="price-per-person-netto-row">
                                        <UiTableCell fontWeight="medium">
                                            <span>{t('Price per participant')} ({t('netto')})</span>
                                        </UiTableCell>
                                        <UiTableCell align="right">
                                            {currencyNumberFormat(nettoPerPerson)}
                                        </UiTableCell>
                                        <UiTableCell>
                                            <span />
                                        </UiTableCell>
                                    </UiTableRow>
                                </>
                            )
                        })()}
                        {packagesCart.packagesCartItems.length === 0 && (
                            <UiTableRow key="no-items">
                                <UiTableCell align="center" colSpan={3}>
                                    {t('No items')}
                                </UiTableCell>
                            </UiTableRow>
                        )}
                    </UiTableBody>
                </UiTable>
            </div>

            {editingOfferItem && editingPresentation && (
                <PackageUpsellSlideOver
                    isOpen={true}
                    title={editingPresentation.title}
                    descriptionHtml={editingPresentation.descriptionHtml}
                    imageSrc={editingPresentation.imageSrc}
                    groupLabel={editingPresentation.groupLabel}
                    unitPrice={currencyNumberFormat(Math.round(editingUnitPrice * 100))}
                    totalPrice={currencyNumberFormat(Math.round(editingTotalPrice * 100))}
                    inCartCount={editingOfferItem.drivers_count_adult + editingOfferItem.drivers_count_child}
                    detailsSlot={
                        <UiRoundedContent className={"BookingSellerRecapitulationItem"}>
                            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700">
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                    {t('Item Details')}
                                </p>
                            </div>
                            <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <div className="sm:col-span-1">
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                        {t('Quantity')}
                                    </span>
                                    <div className="mt-2">
                                        <UiCounter
                                            value={editingQuantity}
                                            onChange={(val) => setEditingQuantity(val === '' ? 0 : Number(val))}
                                            min={0}
                                        />
                                    </div>
                                </div>
                                <div className="sm:col-span-1">
                                    <UiCurrencyInput
                                        label={t('Unit Price')}
                                        value={editingUnitPrice}
                                        onValueChange={(value: string | undefined) => {
                                            const numValue = value === undefined ? 0 : parseFloat(value)
                                            setEditingUnitPrice(isNaN(numValue) ? 0 : numValue)
                                        }}
                                        prefix="CHF "
                                        decimalsLimit={2}
                                    />
                                </div>
                                <div className="sm:col-span-1">
                                    <UiCurrencyInput
                                        label={t('Total Price')}
                                        value={editingTotalPrice}
                                        prefix="CHF "
                                        decimalsLimit={2}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </UiRoundedContent>
                    }
                    customTextSlot={
                        <UiRoundedContent className={"BookingSellerRecapitulationPackageCartItem"}>
                            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                    {t('Custom Text')}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const description = editingPresentation.custom_text
                                            ? editingPresentation.custom_text
                                            : editingPresentation.descriptionHtml
                                        setEditingCustomText(description)
                                    }}
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    {t('Auto fill')}
                                </button>
                            </div>
                            <div className="p-3">
                                <UiTextarea
                                    value={editingCustomText}
                                    onChange={(event) => setEditingCustomText(event.target.value)}
                                    placeholder={t('Enter custom text...')}
                                    rows={10}
                                />
                            </div>
                        </UiRoundedContent>
                    }
                    actionsSlot={
                        <button
                            className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition disabled:opacity-50"
                            type="button"
                            disabled={isSavingEditItem}
                            onClick={() => void saveInlineEditPopup()}
                        >
                            {isSavingEditItem ? t('Saving...') : t('Save')}
                        </button>
                    }
                    onClose={() => setEditingOfferItem(null)}
                />
            )}

            <Tooltip id={OFFER_DETAIL_CALC_EDIT_DISABLED_TOOLTIP_ID} style={{ zIndex: 99999 }} />
        </div>
    )
}

export default BookingSellerRecapitulationPackageCartItemsTable;
